<template>
    <Loading :v-if="loading" :loading="loading" :inset="true" text="Verarbeite Ihren Kauf...">
        <div class="group relative bg-white rounded-lg">
            <!-- Produktbild -->
            <div class="relative flex flex-row border-b border-gray-200 py-4 lg:p-5 items-center lg:gap-5 gap-3">
                <img
                    v-if="product.image"
                    :src="product.image"
                    :alt="product.imageAlt || product.name"
                    class="w-20 object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                    decoding="async"
                />
                <!-- Name & Beschreibung -->
                <div class="lg:mb-3 mb-0">
                    <h3 class="text-lg sm:text-xl font-bold text-gray-900 mb-1 line-clamp-2">
                        {{ product.name.de }}
                    </h3>
                    <!-- product Attribute title -->
                    <p class="text-sm text-gray-600 line-clamp-2">Laufzeit: {{ product.period.de }}</p>
                </div>
            </div>

            <!--Show Error-->
            <div v-if="errorMsg" class="text-red font-bold text-sm my-4">{{ errorMsg }}</div>

            <!-- Produktinhalt -->
            <div class="py-4 sm:p-5">
                <h3 class="text-lg sm:text-xl font-bold text-gray-900 mb-1 line-clamp-2">Zahlungsmethode:</h3>
                <!--paymentmethod-->
                <SinglePaymentMethod
                    v-for="paymentMethod in paymentMethods"
                    :key="paymentMethod.id"
                    :payment-method="paymentMethod"
                    :show-toggle="true"
                    :active="selectedExistingPaymentMethod === paymentMethod.id"
                    @click.stop="selectExistingPaymentMethod(paymentMethod.id)"
                />

                <!--Link zu Paymentmethods-->
                <div class="mt-8">
                    <router-link to="/profile/paymentmethods" class="btn bg-blue-dark text-sm text-white"> Zahlungsmethoden verwalten </router-link>
                </div>
            </div>
            <!-- Preis -->
            <div class="py-3 sm:p-5 border-t border-gray-200">
                <h3 v-if="selectedVariant?.webinar" class="text-lg sm:text-xl font-bold text-gray-900 mb-1 line-clamp-2">Preisübersicht</h3>

                <div class="flex flex-col sm:grid-cols-2 gap-4 mb-4">
                    <!--Preis auflistung-->
                    <div v-if="selectedVariant?.webinar" class="flex flex-row gap-8">
                        <div>
                            <span class="text-xs text-gray-500 block mb-1">Preis Basis</span>
                            <span class="text-2xl font-bold text-blue-600">
                                {{ formatCurrency(selectedVariant.price) }}
                            </span>
                        </div>
                        <!--if webinar -->
                        <div v-if="selectedVariant.webinar">
                            <span class="text-xs text-gray-500 block mb-1">Preis Webinar</span>
                            <span class="text-2xl font-bold text-blue-600">
                                {{ formatCurrency(selectedVariant.webinar.price) }}
                            </span>
                        </div>
                    </div>
                    <!--Preis Total-->
                    <div>
                        <span class="text-xl block mb-1">Gesamtpreis</span>
                        <span class="text-2xl font-bold text-blue-600">
                            {{ formatCurrency(currentPrice) }}
                        </span>
                    </div>
                </div>

                <!--Gift Message text field-->
                <div class="flex flex-col">
                    <label for="giftMessage" class="text-sm text-gray-700 mb-1">Geschenk-Nachricht (optional)</label>
                    <textarea
                        v-model="giftMessage"
                        rows="1"
                        class="border border-gray-300 rounded-md p-2 w-full max-w-full min-w-full resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Fügen Sie eine persönliche Nachricht hinzu..."
                    ></textarea>
                </div>
                <div id="terms" class="my-4">
                    <div class="my-4">
                        <Checkbox v-model:value="acceptedConditions" name="conditions" :label="conditionsText" class="m-0 my-2 text-xs" />
                        <div v-if="conditionsNotAcceptedError" class="mt-2 text-red text-xs">
                            <p>
                                {{ $t('accept_conditions') }}
                            </p>
                        </div>
                    </div>
                    <div class="my-4">
                        <Checkbox v-model:value="acceptedTermsConditions" name="terms_and_conditions" :label="termsText" class="m-0 my-2 text-xs" />
                        <div v-if="termsNotAcceptedError" class="mt-2 text-red text-xs">
                            <p>
                                {{ $t('accept_terms_and_conditions') }}
                            </p>
                        </div>
                    </div>
                </div>
                <!-- Kaufen Button -->
                <button
                    :class="[
                        'btn primary flex flex-row gap-2 items-center w-full text-center justify-center mt-6',
                        { 'cursor-not-allowed opacity-50': !acceptedTermsConditions || !acceptedConditions }
                    ]"
                    @click.stop="handleBuyClick()"
                >
                    <span class=""> Jetzt zahlungspflichtig kaufen </span>
                </button>
            </div>
        </div>
    </Loading>
</template>

<script setup>
import axios from 'axios';
import i18next from 'i18next';
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import SinglePaymentMethod from './customer/SinglePaymentMethod.vue';
import Loading from './miscellaneous/Loading.vue';

const props = defineProps({
    product: {
        type: Object,
        required: true,
        validator: (value) => {
            return value.id && value.name && value.price !== undefined;
        }
    }
});
const emit = defineEmits(['buy']);
const termsText = i18next.t('contract_execution_before_cancellation');
const conditionsText = i18next.t('terms_conditions_consent');

const acceptedConditions = ref(false);
const acceptedTermsConditions = ref(false);

const conditionsNotAcceptedError = ref(false);
const termsNotAcceptedError = ref(false);

const selectedVariant = ref(null);
const store = useStore();
const selectedExistingPaymentMethod = ref(null);
const giftMessage = ref('');
const errorMsg = ref('');
const loading = ref(false);
const paymentMethods = computed(() => getPaymentMethods() ?? []);

const getPaymentMethods = () => {
    let usePaymentMethods = store.getters['payment_methods/get'].data;

    if (!usePaymentMethods || usePaymentMethods.length === 0) {
        store.dispatch('payment_methods/index').then(async () => {
            usePaymentMethods = await store.getters['payment_methods/get'].data;
        });
    }

    return usePaymentMethods;
};

const selectExistingPaymentMethod = (id) => {
    selectedExistingPaymentMethod.value = id;
};

if (props.product) {
    selectedVariant.value = props.product;
}

/**
 * Aktueller Preis basierend auf ausgewählter Variante oder Basispreis
 */
const currentPrice = computed(() => {
    if (selectedVariant.value?.webinar) {
        return selectedVariant.value.webinar.price + selectedVariant.value.price;
    }

    return selectedVariant.value.price;
});

/**
 * Formatiert Betrag als Euro
 */
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR'
    }).format(amount);
};

/**
 * Handler für Kaufen-Button
 */
const handleBuyClick = async () => {
    const payload = {
        userPaymentId: selectedExistingPaymentMethod.value,
        productVariantId: props.product.id,
        webinarId: selectedVariant.value.webinar ? selectedVariant.value.webinar.id : null,
        text: giftMessage.value || null
    };

    if (!payload.productVariantId) {
        errorMsg.value = 'Ungültige Produktvariante ausgewählt.';

        return;
    }

    if (!payload.userPaymentId) {
        errorMsg.value = 'Bitte wählen Sie eine Gültige Zahlungsmethode aus.';

        return;
    }

    if (!payload.userPaymentId) {
        errorMsg.value = 'Bitte wählen Sie eine Gültige Zahlungsmethode aus.';

        return;
    }

    if (acceptedTermsConditions.value === false || acceptedConditions.value === false) {
        if (acceptedTermsConditions.value === false) termsNotAcceptedError.value = true;

        if (acceptedConditions.value === false) conditionsNotAcceptedError.value = true;

        this.$scrollToId('#terms');

        return;
    }

    loading.value = true;

    try {
        await axios.post('/api/v1/giftcode/pay', payload).then((response) => {
            if (response.data?.success) {
                loading.value = false;

                emit('buy', response.data);
            }
        });
    } catch (error) {
        loading.value = false;
        errorMsg.value = 'Beim Kauf ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.';
    }
};
</script>

<style scoped>
textarea {
    resize: both;
}
</style>
